import React from 'react'
import PropTypes from 'prop-types'

/**
 * @typedef {object} ListingCarouselProps
 * @property {Array} [listings]
 * @property {{ next?: Function, back?: Function, save?: Function, click?: Function }} [analytics]
 * @property {object} [settings]
 * @property {string} [elmId]
 * @property {object} [arrowIconPropsSettings]
 * @property {boolean} [showSave]
 * @property {Function} [onSaveToggle]
 * @property {object} [featureFlags]
 */
import { noop, bindKey } from 'lodash'
import {
  Carousel,
  NextArrow,
  BackArrow,
  ListingCard,
  ListingCardViewModeEnum,
} from '@adc/ui-core'
import _cx from 'classnames/bind'
import cls from './styles.scss'
const cx = _cx.bind(cls)

const nextArrowIconProps = {
  color: 'black',
  size: 'large',
  iconStyle: 'solid',
  className: cx('arrow', 'arrow-shadow'),
}
const backArrowIconProps = {
  color: 'black',
  size: 'large',
  iconStyle: 'solid',
  className: cx('arrow', 'arrow-shadow', 'back-arrow'),
}

// TODO this propType was brought here because we removed JS version of listing card, once we migrate to TS please remove this consts.
// BE AWARE BEFORE REMOVING, it's also being used in ListingPhotoGrid.
export const listingAddressPropTypes = PropTypes.shape({
  street_description: PropTypes.string,
  municipality: PropTypes.string,
  country_primary_subdivision: PropTypes.string,
  country_secondary_subdivision: PropTypes.string,
  postal_code: PropTypes.string,
})

export const listingPropType = PropTypes.shape({
  listing_id: PropTypes.string.isRequired,
  primary_photo: PropTypes.string,
  listing_photos_count: PropTypes.number,
  listing_page_path: PropTypes.string.isRequired,
  reserve_price: PropTypes.number,
  is_hot: PropTypes.bool,
  listing_status_label: PropTypes.string.isRequired,
  listing_configuration: PropTypes.shape({
    product_type: PropTypes.string.isRequired,
    broker_commission: PropTypes.number,
    financing_available: PropTypes.bool,
    buyer_premium_available: PropTypes.bool,
    interior_access_allowed: PropTypes.bool,
    occupancy_status: PropTypes.string,
    is_reserve_displayed: PropTypes.bool,
    mlh_seller_code: PropTypes.string,
    asset_type: PropTypes.string,
    is_direct_offer_enabled: PropTypes.bool,
    is_third_party_online: PropTypes.bool,
  }),
  listing_summary: PropTypes.shape({
    is_remote_bid_enabled: PropTypes.bool,
    is_remote_before_and_during_auction_enabled: PropTypes.bool,
    show_opening_bid: PropTypes.bool,
  }),
  event: PropTypes.shape({
    event_code: PropTypes.string,
  }),
  seller_property: listingAddressPropTypes,
  primary_property: PropTypes.shape({
    property_id: PropTypes.string,
    summary: PropTypes.shape({
      street_address: PropTypes.string,
      total_bedrooms: PropTypes.number,
      total_bathrooms: PropTypes.number,
      square_footage: PropTypes.number,
      lot_size: PropTypes.number,
      year_built: PropTypes.number,
      valuation: PropTypes.number,
      structure_type_code: PropTypes.string,
    }),
    is_currently_saved: PropTypes.bool,
    is_newly_listed: PropTypes.bool,
    current_user_tracking_state: PropTypes.shape({
      is_seen: PropTypes.bool,
      is_updated: PropTypes.bool,
    }),
  }),
  auction: PropTypes.shape({
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    starting_bid: PropTypes.number,
    is_online: PropTypes.bool,
    bid_instruction: PropTypes.shape({
      nos_amount: PropTypes.number,
    }),
  }),
  parties: PropTypes.arrayOf(
    PropTypes.shape({
      party_role: PropTypes.string,
      party_name: PropTypes.string,
    })
  ),
  external_information: PropTypes.shape({
    collateral: PropTypes.shape({
      summary: PropTypes.arrayOf(
        PropTypes.shape({
          estimate: PropTypes.number,
          high: PropTypes.number,
          type: PropTypes.string,
        })
      ),
    }),
  }),
})

const ListingCarousel = ({
  listings,
  analytics,
  settings = {},
  elmId,
  arrowIconPropsSettings = {},
  showSave,
  onSaveToggle,
  featureFlags,
}) => {
  const defaultAnalytics = {
    next: noop,
    back: noop,
    save: noop,
    click: noop,
    hover: noop,
  }
  const finalAnalytics = Object.assign({}, defaultAnalytics, analytics)
  const finalNextArrowIconProps = Object.assign(
    {},
    nextArrowIconProps,
    arrowIconPropsSettings
  )
  const finalBackArrowIconProps = Object.assign(
    {},
    backArrowIconProps,
    arrowIconPropsSettings
  )
  const carouselSettings = {
    infinite: true,
    slidesToScroll: 1,
    initialSlide: 0,
    variableWidth: true,
    nextArrow: (
      <NextArrow
        next={finalAnalytics.next}
        arrowIconProps={finalNextArrowIconProps}
        alwaysShowArrows
      />
    ),
    prevArrow: (
      <BackArrow
        back={finalAnalytics.back}
        arrowIconProps={finalBackArrowIconProps}
        alwaysShowArrows
      />
    ),
  }
  const finalSettings = Object.assign({}, carouselSettings, settings)

  return (
    <div className={cx('root')}>
      <Carousel
        settings={finalSettings}
        elmId={elmId}
        back={finalAnalytics.back}
        next={finalAnalytics.next}
      >
        {listings?.map((listing, i) => (
          <div
            key={`listing_card_${i}`}
            className={cx('u-mr-1', 'u-ml-1', 'u-pb-1')}
            data-target-card-index={`${i}`}
            data-target-id={`${listing.listing_id}`}
            data-target-property-id={`${listing.primary_property.property_id}`}
            data-target-product-type={`${listing.listing_configuration.product_type}`}
          >
            <ListingCard
              elmId={`${elmId}_listing_card_${i}`}
              listing={listing}
              showSave={showSave}
              onSaveToggle={onSaveToggle}
              viewMode={ListingCardViewModeEnum.GRID}
              linkProps={{
                href: listing.listing_page_path,
                onClick: bindKey(finalAnalytics, 'click', i),
                onMouseLeave: bindKey(finalAnalytics, 'hover', i),
              }}
              featureFlags={featureFlags}
            />
          </div>
        ))}
      </Carousel>
    </div>
  )
}

export default ListingCarousel
